<template>
    <div id="chartTemplates">
        <div v-if="view === 'show'">
            <div class="border border-t-0 mt-6 mb-4">
                <div class="flex flex-wrap bg-tan-200 border-t-2 border-blue-600 px-3 pt-2 md:pb-0 pb-2">
                    <div class="inline-block sm:mr-4 mb-2 xs:w-full">
                        <label for="default-template">
                            Default Template:
                            <select @change="updateDefaultTemplate" class="form-control form-control-sm form-control-fixed bg-white" id="default-template" v-model="form.defaultTemplate">
                                <option value="none">None</option>
                                <optgroup label="Your templates">
                                    <option :value="template" v-for="template in templates">{{ template.name }}</option>
                                </optgroup>
                                <optgroup label="Jim's templates">
                                    <option :value="template" v-for="template in jimsTemplates">{{ template.name }}</option>
                                </optgroup>
                            </select>
                        </label>
                    </div>
                    <div class="inline-block sm:mr-4 mb-2 xs:w-full">
                        <label for="feed">Update Chart:</label>
                        <select @change="updateDefaultFeed" class="form-control form-control-sm form-control-fixed bg-white" v-model="form.feedMode" name="feed" id="feed">
                            <option value="realtime">Auto-Update</option>
                            <option value="eod">End-of-Day</option>
                        </select>
                    </div>

                    <div class="flex ml-auto mr-auto md:mr-0 mb-1 sm:mb-2 border-t md:border-t-0 pt-3 md:pt-0 md:pl-4 md:border-l border-gray-400 w-full md:w-auto mt-1 md:mt-0 print:hidden">
                        <div class="btn btn-sm btn-blue mx-auto md:mx-0" @click="createTemplate"><i class="fas fa-plus"></i>&nbsp;Create New Template</div>
                    </div>
                </div>
            </div>

            <div v-if="templates.length === 0">
                <alert-box class="mb-4">
                    <template #body>You do not have any chart templates.</template>
                </alert-box>
            </div>

            <div class="border border-t-0 mb-4 table-responsive" v-else>
                <table class="table table-striped-01 table-hover border-t-2 border-blue-600">
                    <thead>
                        <tr class="bg-tan-200">
                            <th class="text-center print:hidden">Sort</th>
                            <th class="w-1/2">Name</th>
                            <th class="w-1/2">Description</th>
                            <th class="text-center" v-if="isJim()">Public</th>
                            <th class="text-center print:hidden">Delete</th>
                        </tr>
                    </thead>
                    <draggable v-model="templates" @update="updateOrder" tag="tbody" handle=".canDrag" item-key="id">
                        <template #item="{ element, index }">
                            <tr class="cursor-pointer">
                                <td class="text-center cursor-move canDrag print:hidden">
                                    <i class="fas fa-arrows-alt text-gray-500"></i>
                                </td>
                                <td>
                                    <div class="text-blue-500 no-underline" @click="selectTemplate(element)">
                                        {{ element.name }}
                                    </div>
                                </td>
                                <td>
                                    {{ element.description }}
                                </td>
                                <td v-if="isJim()">
                                    {{ element.public ? 'Yes' : 'No' }}
                                </td>
                                <td class="text-center print:hidden">
                                    <a @click="showDeleteTemplateModal(element)" v-tooltip="{ content: 'Delete Current Chart Template', placement: 'auto-start', theme: 'red' }"  class="btn btn-default btn-shadow-none btn-red-ghost btn-xs ml-1 mb-1 md:mb-0" title="Delete Chart Template">
                                        <i class="fas fa-times"></i>
                                    </a>
                                </td>
                            </tr>
                        </template>
                    </draggable>
                </table>
            </div>

            <div class="border border-t-0" v-if="jimsTemplates && !isAdmin">
                <table class="table table-striped-01 table-hover border-t-2 border-blue-600">
                    <thead>
                        <tr class="bg-tan-200">
                            <th colspan="2">Jim's Templates <span class="xs:w-full xs:block font-normal">(click on template to view or copy)</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="template in jimsTemplates">
                            <td>
                                <div class="text-blue-500 no-underline cursor-pointer" @click="selectTemplate(template)">
                                    {{ template.name }}
                                </div>
                            </td>
                            <td>Jim Prince's Public Template</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <modal @close="handleClose" v-if="chartTemplateModal === 'delete'" className="max-w-sm w-full">
                <template #header>
                    <div class="text-red-500">
                        <i class="fas fa-exclamation-circle mr-1"></i>
                        Delete Chart Template
                    </div>
                </template>
                <template #body>
                    <div class="mb-3 pt-4 pb-2">
                        <p>Are you sure you want to delete your template <strong>{{ selectedTemplate.name }}</strong>?</p>
                        <p v-if="selectedIsDefault()" class="mt-2">It is currently being used as your default template.</p>
                        <p class="mt-2">Changes can not be undone.</p>
                    </div>
                </template>

                <template #footer>
                    <div class="flex justify-center">
                        <div class="btn btn-sm btn-red-ghost inline-flex mx-1" @click="handleClose">Cancel</div>
                        <div class="btn btn-sm btn-red inline-flex mx-1" @click="deleteTemplate">Delete</div>
                    </div>
                </template>
            </modal>
        </div>

        <div v-if="view === 'create' || view === 'edit'">
            <chart-template-form
                :is-admin="isAdmin"
                :headless-chart="headlessChart"
                :is-jims="selectedTemplate ? selectedTemplate.public : false"
                :template="selectedTemplate || {}"
                @copy="copyJimsTemplate"
                @update="updateTemplate"
                @save="saveTemplate"
                @cancel="cancel"
            ></chart-template-form>
        </div>

        <flash-message secondsToDisplay="3" ref="flash"></flash-message>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import Draggable from 'vuedraggable';
import ChartTemplateForm from './ChartTemplateForm';
import { useIsJim } from '../../mixins/IsJim';

// Define props
const props = defineProps({
    // The users templates
    userTemplates: Array,

    // The default templates for the user
    userDefaultTemplates: Object,
    jimsTemplates: Array,
    isAdmin: Boolean,
    defaultFeedMode: String
});

const { isJim } = useIsJim();
const flash = ref(null);

const templates = ref(props.userTemplates);
const selectedTemplate = ref({});
const view = ref('show');
const form = ref({
    defaultTemplate: props.userDefaultTemplates.default_template || 'none',
    defaultFlipChartsTemplate: props.userDefaultTemplates.default_flipcharts_template || 'none',
    feedMode: props.defaultFeedMode || 'realtime'
});
const chartTemplateModal = ref(null);

// Used for chart templates
const headlessChart = ref(null);

// Methods
const createTemplate = () => {
    view.value = 'create';
};

const selectTemplate = (template) => {
    selectedTemplate.value = template;
    view.value = 'edit';
};

const clearDefault = () => {
    form.value.defaultTemplate = null;
};

const cancel = () => {
    selectedTemplate.value = {};
    headlessChart.value.reset();
    view.value = 'show';
};

// Updates a single template record
const update = () => {
    flash.value.display('Saved changes');

    const route = window.route('my-folder.chart-templates.update');
    axios.put(route, selectedTemplate.value)
        .then((response) => {
            flash.value.display('Saved changes');
        }).catch((error) => {
            flash.value.error('Error updating template order');
        }).then(() => {
        });
};

const copyJimsTemplate = (template, newName) => {
    const route = window.route('my-folder.chart-templates.copy', template);

    axios.post(route, { name: newName })
        .then((response) => {
            flash.value.display('Saved changes');

            if (response.data.success) {
                templates.value.push(response.data.template);
            }

            view.value = 'show';
        }).catch((error) => {
            flash.value.error(`Error copying Jim's template`);
        }).then(() => {
        });
};

const saveTemplate = (template) => {
    const route = window.route('my-folder.chart-templates.store');

    axios.post(route, template)
        .then((response) => {
            flash.value.display(`Created new template ${template.name}`);
            templates.value.push(response.data);
        }).catch((error) => {
            flash.value.error(`Error creating your new template`);
        }).then(() => {
            view.value = 'show';
        })
};

const updateTemplate = (template) => {
    const route = window.route('my-folder.chart-templates.update', template);

    axios.patch(route, template)
        .then((response) => {
            flash.value.display(`Updated template ${template.name}`);

        }).catch((error) => {

            flash.value.error('Error updating default template');
        }).then(() => {
            const updatedTemplate = templates.value.find(t => t.id === template.id);

            updatedTemplate.name = template.name;
            updatedTemplate.description = template.description;
            updatedTemplate.template = template.template;

            view.value = 'show';
        });
};

// Updates all template records with the correct sort order
const updateOrder = () => {
    const route = window.route('my-folder.chart-templates.order');

    axios.post(route, { templates: templates.value })
        .then((response) => {
            flash.value.display('Saved changes');
        }).catch((error) => {

            flash.value.error('Error updating order');
        }).then(() => {

        });
};

const updateDefaultTemplate = (type) => {
    const route = window.route('api.preferences');

    const params = {
        default_template: (form.value.defaultTemplate.id) || null
    };

    axios.post(route, params)
        .then((response) => {
            flash.value.display('Updated default template');

        }).catch((error) => {

            flash.value.error('Error updating default template');
        }).then(() => {

        });
};

const updateDefaultFeed = () => {
    const route = window.route('api.preferences');

    const params = {
        default_feed_mode: form.value.feedMode
    };

    axios.post(route, params)
        .then((response) => {
            flash.value.display('Updated default chart setting');

        }).catch((error) => {

            flash.value.error('Error updating default chart setting');
        }).then(() => {

        });
};

const selectedIsDefault = () => {
    return selectedIsChartDefault();
};

const selectedIsChartDefault = () => {
    return selectedTemplate.value.id === form.value.defaultTemplate.id;
};

const showDeleteTemplateModal = (template) => {
    chartTemplateModal.value = 'delete';
    selectedTemplate.value = template;
};

const deleteTemplate = () => {
    const route = window.route('my-folder.chart-templates.destroy', selectedTemplate.value.id);

    axios.delete(route)
        .then((response) => {
            flash.value.display('Template deleted');

            // Remove from local state
            templates.value = templates.value.filter(template => template.id !== selectedTemplate.value.id);

            if (selectedIsChartDefault()) {
                form.value.defaultTemplate = 'none';
            }

        }).catch((error) => {
            flash.value.error('Error deleting template');
            console.error(error);
        }).then(() => {
            selectedTemplate.value = null;
            chartTemplateModal.value = null;
        });
};

const handleClose = () => {
    chartTemplateModal.value = null
};

onMounted(() => {
    const charts = window.Barchart.RealtimeWidgets.Charts;
    const feed = charts.initFeed(charts.BarchartSiteDataFeed, { mode: 'GBE' });

    feed.ready().then(() => {
        headlessChart.value = feed.getHeadlessChart();
    });
});

</script>

<style lang="scss">
#chartTemplates {
    table {
        thead {
            td, th {
                @apply px-3 py-2;
            }
        }
        tbody {
            td, th {
                @apply px-3;
            }
        }
    }
}
</style>
